/**
 * What a narrowed surface is showing, in the one form nothing else says.
 *
 * Every surface that filters tasks needs this sentence, and each had written
 * its own: the board's `boardSubtitle`, the task list's count line, and — on
 * Overview — nothing at all, so a reader who narrowed that page and found it
 * short had no on-screen answer for why.
 *
 * It reports the **result**, never the filters. The board's earlier version
 * restated them — "Showing labelled Add lightning, Clean, Zoo, Feed" — directly
 * beneath the chips naming those same four labels and the pills showing which
 * state was pressed. Every word of it was a second copy of a control a few
 * pixels above, and it grew longer the more filters were applied, which is
 * exactly backwards: the more you narrow, the more you want to see what came
 * back rather than a recital of what you just asked for.
 *
 * `isNarrowed` is passed rather than inferred from `shown !== total`, because a
 * filter that happens to match everything is still on, and a sentence that
 * silently dropped the "of" would tell the reader their filter had come off.
 */
export function narrowingSummary(
  isNarrowed: boolean,
  shown: number,
  total: number,
  /**
   * What the *unnarrowed* count is counting. The board says "3 active tasks",
   * because a board's total is its active tasks and saying so is the difference
   * between a count and a count of something.
   *
   * Deliberately not applied to the narrowed form. "1 of 3 tasks" is a ratio,
   * and a ratio wants the plainest noun it can have — qualifying both halves
   * reads as though the qualifier were part of what was filtered.
   */
  noun = 'task',
): string {
  if (isNarrowed) {
    return `${String(shown)} of ${String(total)} ${total === 1 ? 'task' : 'tasks'}`;
  }
  const plural = total === 1 ? noun : `${noun}s`;
  if (total === 0) return `No ${noun}s`;
  return `${String(total)} ${plural}`;
}
